import { createGesture, Gesture, GestureDetail } from "@ionic/react";
import customBackAnimation from "../NavigationAnimation/customBackAnimation";

export const clamp = (min: number, n: number, max: number) => {
    return Math.max(min, Math.min(n, max));
};

export const isRTL = (hostEl?: Pick<HTMLElement, 'dir'>) => {
    if (hostEl) {
        if (hostEl.dir !== '') {
            return hostEl.dir.toLowerCase() === 'rtl';
        }
    }
    return document?.dir.toLowerCase() === 'rtl';
};

const noSwipeSelectors = [
    '.tabs-container',
    '.video-tag-display',
    '.shop-container',
    '.map-container'
];

export const createSwipeBackGesture = (
    el: HTMLElement,
    canStartHandler: () => boolean,
    onStartHandler: () => void,
    onMoveHandler: (step: number) => void,
    onEndHandler: (shouldComplete: boolean, step: number, dur: number) => void
): Gesture => {
    const win = el.ownerDocument!.defaultView!;
    let rtl = isRTL(el);
    let animation: any;

    const isAtEdge = (detail: GestureDetail) => {
        const threshold = win.innerWidth * 0.75; // 75% of screen width
        const { startX } = detail;
        if (rtl) {
            return startX >= win.innerWidth - threshold;
        }
        return startX <= threshold;
    };

    const getDeltaX = (detail: GestureDetail) => {
        return rtl ? -detail.deltaX : detail.deltaX;
    };

    const getVelocityX = (detail: GestureDetail) => {
        return rtl ? -detail.velocityX : detail.velocityX;
    };

    const canStart = (detail: GestureDetail) => {
        rtl = isRTL(el);

        // Get the element that was touched
        const target = detail.event.target as HTMLElement;

        // Check if the touch target is within any of the no-swipe areas
        const isInNoSwipeArea = noSwipeSelectors.some(selector =>
            target.closest(selector) !== null
        );

        // Only allow swipe if we're at the edge AND not in any no-swipe area
        return isAtEdge(detail) && !isInNoSwipeArea && canStartHandler();
    };

    const onStart = () => {
        animation = customBackAnimation(el, {
            direction: 'back',
            enteringEl: el.children[0] as HTMLElement,
            leavingEl: el.children[1] as HTMLElement
        });
        if (animation) {
            animation.progressStart(true, 0);
            onStartHandler();
        }
    };

    const onMove = (detail: GestureDetail) => {
        const delta = getDeltaX(detail);
        const stepValue = delta / win.innerWidth;
        if (animation) {
            animation.progressStep(stepValue);
            onMoveHandler(stepValue);
        }
    };

    const onEnd = (detail: GestureDetail) => {
        const delta = getDeltaX(detail);
        const width = win.innerWidth;
        const stepValue = delta / width;
        const velocity = getVelocityX(detail);
        const z = width / 2.0;

        const shouldComplete = velocity >= 0 && (velocity > 0.1 || delta > z * 0.7);

        const missing = shouldComplete ? 1 - stepValue : stepValue;
        const missingDistance = missing * width;
        let realDur = 0;
        if (missingDistance > 5) {
            const dur = missingDistance / Math.abs(velocity);
            // Cap at 200ms for snappy feel
            realDur = Math.min(dur, 200);
        }

        if (animation) {
            const enteringEl = el.children[0] as HTMLElement;
            const leavingEl = el.children[1] as HTMLElement;

            const resetElements = () => {
                // Reset any lingering styles on the elements
                if (enteringEl) {
                    enteringEl.style.removeProperty('transform');
                    enteringEl.style.removeProperty('opacity');
                    enteringEl.style.removeProperty('z-index');
                    enteringEl.style.removeProperty('transform-origin');
                }
                if (leavingEl) {
                    leavingEl.style.removeProperty('transform');
                    leavingEl.style.removeProperty('opacity');
                    leavingEl.style.removeProperty('z-index');
                    leavingEl.style.removeProperty('transform-origin');
                    leavingEl.style.removeProperty('pointer-events');
                }
            };

            if (shouldComplete) {
                // Complete the animation to end position
                animation.progressEnd(1, stepValue, realDur);

                // Navigate immediately - the animation will continue visually
                // while the route updates in the background
                onEndHandler(shouldComplete, stepValue <= 0 ? 0.01 : clamp(0, stepValue, 0.9999), realDur);

                // Clean up animation after it finishes
                animation.onFinish(() => {
                    animation?.stop();
                    animation = null;
                }, { oneTimeCallback: true });
            } else {
                // When cancelled, animate back to the start position
                animation.progressEnd(0, stepValue, realDur);

                // Ensure elements are properly restored after animation reverses
                animation.onFinish(() => {
                    resetElements();
                    animation?.stop();
                    animation = null;
                }, { oneTimeCallback: true });

                // Fallback timeout in case onFinish doesn't fire properly
                const fallbackDur = realDur + 100;
                setTimeout(() => {
                    resetElements();
                    if (animation) {
                        animation?.stop();
                        animation = null;
                    }
                }, fallbackDur);

                onEndHandler(shouldComplete, stepValue <= 0 ? 0.01 : clamp(0, stepValue, 0.9999), realDur);
            }
        }
    };

    return createGesture({
        el,
        gestureName: 'goback-swipe',
        gesturePriority: 101,
        threshold: 5,
        canStart,
        onStart,
        onMove,
        onEnd,
    });
};